import { FC, useMemo } from 'react'
import {
  CloudFunctionResponseCodes,
  PaddleProduct,
  PlanType,
  ServerPlanType,
  useGeneratePaddleCheckoutUrl,
} from '@expane/data'
import { useTranslation } from 'react-i18next'
import { AppSubscriptionProductCard, useSnackbar } from '@expane/widgets'
import { normalizeAppSubscriptionProducts } from '@expane/logic/appSubscription'
import { useShowCustomWarningPopup } from '@expane/ui'
import { EXPANE_NETWORKS } from '@expane/logic/links'

export const AppSubscriptionProducts: FC<{
  products: PaddleProduct[]
  planTypes: ServerPlanType[]
  activePlanType?: string | undefined
  employeeCount: number
}> = ({ products, planTypes, activePlanType, employeeCount }) => {
  const { t } = useTranslation()

  const { mutateAsync: generatePaddleCheckoutUrl, isLoading } = useGeneratePaddleCheckoutUrl()

  const [openSnackbar] = useSnackbar()
  const { showCustomWarningPopup, customWarningModal } = useShowCustomWarningPopup()

  const filteredProducts = useMemo(() => normalizeAppSubscriptionProducts(products), [products])

  const canBuyAppSubscription = !activePlanType // && !alreadySuccessCheckout

  const onBuyAppSubscription = async (priceId: string, planType: PlanType) => {
    if (employeeCount > 1) {
      const plan = planTypes.find(plan => plan.type === planType)
      if (!plan) return

      const maxEmployees = plan?.maxEmployees

      if (maxEmployees && employeeCount >= maxEmployees) {
        showCustomWarningPopup(t('error'), t('appSubscription.maxEmployeesError'))

        return
      }
    }

    try {
      const result = await generatePaddleCheckoutUrl({ priceId })

      if (
        result &&
        result.generatePaddleCheckoutUrl?.code === CloudFunctionResponseCodes.successful &&
        result.generatePaddleCheckoutUrl?.hostedCheckoutUrl
      ) {
        window.location.href = result.generatePaddleCheckoutUrl.hostedCheckoutUrl
      } else {
        openSnackbar(t('submitError'), 'error')
      }
    } catch (e) {
      reportError(e)
      openSnackbar(t('submitError'), 'error')
    }
  }

  return (
    <div className="mt-10">
      {!filteredProducts.length && (
        <h2 className="text-2xl font-bold text-main-color mb-6">
          {t('appSubscription.productsError')}
        </h2>
      )}

      {filteredProducts.length && (
        <div className="flex w-full gap-6">
          {filteredProducts.map(product => (
            <AppSubscriptionProductCard
              key={product.id}
              product={product}
              planTypes={planTypes}
              activePlanType={activePlanType}
              onPriceSelected={canBuyAppSubscription ? onBuyAppSubscription : undefined}
              disabled={isLoading}
            />
          ))}
        </div>
      )}

      <p className="mt-4 text-main-color text-center">
        {t('appSubscription.contactSupportChat.1')}{' '}
        {/* eslint-disable-next-line react/jsx-no-target-blank */}
        <a
          href={EXPANE_NETWORKS.telegramSupport}
          target={'_blank'}
          className="underline text-primary-500"
        >
          {t('appSubscription.contactSupportChat.2')}
        </a>
      </p>

      {customWarningModal}
    </div>
  )
}
